// Глубокая аналитика: 4 таба — Здоровье / Тренды / Поведение / Долги.

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import type { CategoryTrend, TransactionFilters } from '../api/types';
import { CategoryMark } from '../components/CategoryMark';
import { ScreenHeader } from '../components/ScreenHeader';
import { HealthScoreCard } from '../components/analytics/HealthScoreCard';
import { InsightsList } from '../components/analytics/InsightsList';
import { MetricCard } from '../components/analytics/MetricCard';
import { StrategyCompare } from '../components/analytics/StrategyCompare';
import { WhatIfSlider } from '../components/analytics/WhatIfSlider';
import { BarChart, type BarPoint } from '../components/charts/BarChart';
import { CalendarHeatmap } from '../components/charts/CalendarHeatmap';
import { DayOfWeekChart } from '../components/charts/DayOfWeekChart';
import { DonutChart, type DonutSegment } from '../components/charts/DonutChart';
import { Sankey } from '../components/charts/Sankey';
import { Sparkline } from '../components/charts/Sparkline';
import {
  BankIcon,
  BarsIcon,
  CalendarIcon,
  DropletIcon,
  FlagIcon,
  FlameIcon,
  SearchIcon,
  ShieldIcon,
} from '../components/icons';
import { formatWithCurrency } from '../lib/money';
import { haptic } from '../lib/telegram';
import { useBackButton } from '../lib/use-main-button';

type Tab = 'health' | 'trends' | 'behavior' | 'debts';
type Period = 'week' | 'month' | 'year';
type Kind = 'expense' | 'income';

type RootTab = 'debts' | 'transactions' | 'categories' | 'dashboard';

type Props = {
  onBack: () => void;
  onDrillDown: (filters: TransactionFilters) => void;
  onNavigateRoot?: (tab: RootTab) => void;
};

const CURRENCY = 'UZS';

// Парсер actionDeepLink из инсайтов (apps/bot/src/lib/insights.ts):
//   tab:<root>           — выйти из Analytics на корневую вкладку (debts/transactions/...)
//   analytics:<inner>    — переключить внутренний таб Analytics
//   tx?key=v&...         — drill-down в Транзакции с фильтрами
function parseInsightDeepLink(
  deepLink: string,
):
  | { kind: 'root'; tab: RootTab }
  | { kind: 'inner'; tab: Tab }
  | { kind: 'tx'; filters: TransactionFilters }
  | null {
  if (deepLink.startsWith('tab:')) {
    const t = deepLink.slice(4);
    if (t === 'debts' || t === 'transactions' || t === 'categories' || t === 'dashboard') {
      return { kind: 'root', tab: t };
    }
    return null;
  }
  if (deepLink.startsWith('analytics:')) {
    const t = deepLink.slice('analytics:'.length);
    if (t === 'health' || t === 'trends' || t === 'behavior' || t === 'debts') {
      return { kind: 'inner', tab: t };
    }
    return null;
  }
  if (deepLink.startsWith('tx?')) {
    const params = new URLSearchParams(deepLink.slice(3));
    const filters: TransactionFilters = {};
    const period = params.get('period');
    if (period === 'today' || period === 'week' || period === 'month') filters.period = period;
    const kind = params.get('kind');
    if (kind === 'expense' || kind === 'income') filters.kind = kind;
    const cat = params.get('category');
    if (cat) filters.category = cat;
    const q = params.get('q');
    if (q) filters.q = q;
    return { kind: 'tx', filters };
  }
  return null;
}

export function Analytics({ onBack, onDrillDown, onNavigateRoot }: Props): JSX.Element {
  const [tab, setTab] = useState<Tab>('health');
  useBackButton(onBack);

  const handleInsightAction = (deepLink: string): void => {
    const parsed = parseInsightDeepLink(deepLink);
    if (!parsed) return;
    haptic('light');
    if (parsed.kind === 'root') {
      onNavigateRoot?.(parsed.tab);
    } else if (parsed.kind === 'inner') {
      setTab(parsed.tab);
    } else if (parsed.kind === 'tx') {
      onDrillDown(parsed.filters);
    }
  };

  return (
    <div className="screen">
      <ScreenHeader title="Аналитика" onBack={onBack} />

      <div className="analytics-tabs">
        <TabButton id="health" cur={tab} setCur={setTab} label="Здоровье" />
        <TabButton id="trends" cur={tab} setCur={setTab} label="Тренды" />
        <TabButton id="behavior" cur={tab} setCur={setTab} label="Поведение" />
        <TabButton id="debts" cur={tab} setCur={setTab} label="Долги" />
      </div>

      {tab === 'health' ? <HealthTab onInsightAction={handleInsightAction} /> : null}
      {tab === 'trends' ? <TrendsTab onDrillDown={onDrillDown} /> : null}
      {tab === 'behavior' ? <BehaviorTab /> : null}
      {tab === 'debts' ? <DebtsTab /> : null}
    </div>
  );
}

function TabButton({
  id,
  cur,
  setCur,
  label,
}: {
  id: Tab;
  cur: Tab;
  setCur: (t: Tab) => void;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`a-tab ${cur === id ? 'active' : ''}`}
      onClick={() => {
        haptic('light');
        setCur(id);
      }}
    >
      {label}
    </button>
  );
}

// ============================ HEALTH TAB ====================================

function HealthTab({
  onInsightAction,
}: { onInsightAction: (deepLink: string) => void }): JSX.Element {
  const healthQ = useQuery({ queryKey: ['health-score'], queryFn: () => api.healthScore() });
  const insightsQ = useQuery({ queryKey: ['insights'], queryFn: () => api.insights() });
  const runRateQ = useQuery({ queryKey: ['run-rate'], queryFn: () => api.runRate() });

  if (healthQ.isLoading) return <SkeletonBlock />;
  if (!healthQ.data) return <div className="empty-mini">Нет данных</div>;
  const h = healthQ.data;

  const runwayMonths = (h.runway_months_x10 / 10).toFixed(1);
  const liquidityMonths = (h.liquidity_ratio_x100 / 100).toFixed(1);

  const runwayTone =
    h.runway_months_x10 >= 30 ? 'good' : h.runway_months_x10 >= 10 ? 'warn' : 'bad';

  return (
    <>
      <HealthScoreCard health={h} />

      {/* Сетка была 2×2, и две карточки из четырёх повторяли числа, уже
          сказанные в пояснениях к полосам выше: DTI 16.0% и норма сбережений
          24.6% стояли на одном экране дважды, в двух разных начертаниях и в
          пол-экрана друг от друга. Осталось то, чего в полосах нет. */}
      <div className="metrics-grid">
        <MetricCard
          label="Подушка"
          value={`${runwayMonths} мес`}
          hint={h.runway_months_x10 >= 30 ? 'Цель 3 мес — есть' : 'Цель: 3+ мес'}
          tone={runwayTone}
          icon={<ShieldIcon size={15} />}
        />
        <MetricCard
          label="Ликвидность"
          value={`${liquidityMonths}×`}
          hint="Кэш / месячный расход"
          tone="neutral"
          icon={<DropletIcon size={15} />}
        />
      </div>

      {runRateQ.data ? (
        <div className="run-rate-card">
          <div className="run-rate-head">Прогноз месяца</div>
          {/* «День 15 из 30» стоял отдельной строкой без числа справа — она
              выпадала из столбца и читалась как оборванная. Это не отдельный
              факт, а уточнение к «сейчас потрачено»: столько дней и прошло. */}
          <div className="run-rate-row">
            <span>
              Сейчас потрачено
              <span className="run-rate-sub">
                день {runRateQ.data.days_passed} из {runRateQ.data.days_in_month}
              </span>
            </span>
            <b>{formatWithCurrency(runRateQ.data.current_expense_minor, CURRENCY)}</b>
          </div>
          <div className="run-rate-row run-rate-projection">
            <span>При текущем темпе к концу месяца:</span>
            <b>{formatWithCurrency(runRateQ.data.projected_expense_minor, CURRENCY)}</b>
          </div>
        </div>
      ) : null}

      <div className="section-subtitle">Инсайты</div>
      {insightsQ.data ? (
        <InsightsList insights={insightsQ.data.insights} onAction={onInsightAction} />
      ) : (
        <SkeletonBlock />
      )}
    </>
  );
}

// ============================ TRENDS TAB ====================================

function TrendsTab({ onDrillDown }: { onDrillDown: (f: TransactionFilters) => void }): JSX.Element {
  const [period, setPeriod] = useState<Period>('month');
  const [kind, setKind] = useState<Kind>('expense');

  const tsQ = useQuery({
    queryKey: ['ts', period],
    queryFn: () => api.timeseriesAnalytics(period),
  });
  const trendsQ = useQuery({
    queryKey: ['cat-trends', kind],
    queryFn: () => api.categoryTrends(kind, 6),
  });
  const byCatQ = useQuery({
    queryKey: ['by-cat', period, kind],
    queryFn: () => api.byCategoryAnalytics(period, kind),
  });
  const sankeyQ = useQuery({
    queryKey: ['sankey', period],
    queryFn: () => api.sankey(period),
  });
  const merchantsQ = useQuery({
    queryKey: ['top-merchants', period, kind],
    queryFn: () => api.topMerchants(period, kind, 8),
  });

  const granularity = tsQ.data?.granularity ?? 'day';
  const barPoints: BarPoint[] = (tsQ.data?.points ?? []).map((p) => ({
    bucket: p.bucket,
    label: granularity === 'day' ? bucketDayLabel(p.bucket) : bucketMonthLabel(p.bucket),
    fullLabel: granularity === 'day' ? bucketDayFull(p.bucket) : bucketMonthFull(p.bucket),
    valueMinor: kind === 'expense' ? p.spent_minor : p.earned_minor,
  }));
  const barColor = kind === 'expense' ? 'var(--ink)' : 'var(--positive)';

  const segments: DonutSegment[] = (byCatQ.data?.categories ?? []).map((c) => ({
    key: c.category_slug ?? 'unknown',
    label: c.category_name ?? c.category_slug ?? 'Без категории',
    amountMinor: c.total_minor,
    pct: c.pct,
    slug: c.category_slug,
  }));

  // Pareto: какие первые N категорий дают 80% трат
  let pareto: { count: number; total: number } | null = null;
  if (segments.length > 0) {
    let acc = 0;
    let i = 0;
    for (i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (!seg) continue;
      acc += seg.pct;
      if (acc >= 80) {
        i++;
        break;
      }
    }
    pareto = { count: i, total: segments.length };
  }

  const trends = trendsQ.data?.categories ?? [];

  return (
    <>
      <div className="period-tabs">
        {(['week', 'month', 'year'] as const).map((p) => (
          <button
            key={p}
            type="button"
            className={`period-tab ${period === p ? 'active' : ''}`}
            onClick={() => setPeriod(p)}
          >
            {p === 'week' ? 'Неделя' : p === 'month' ? 'Месяц' : 'Год'}
          </button>
        ))}
      </div>
      <div className="kind-toggle">
        {(['expense', 'income'] as const).map((k) => (
          <button
            key={k}
            type="button"
            className={`kind-btn ${kind === k ? 'active' : ''}`}
            onClick={() => setKind(k)}
          >
            {k === 'expense' ? 'Расходы' : 'Доходы'}
          </button>
        ))}
      </div>

      {tsQ.isLoading ? (
        <SkeletonBlock h={170} />
      ) : (
        <BarChart points={barPoints} currency={CURRENCY} color={barColor} />
      )}

      <div className="section-subtitle">Распределение</div>
      {byCatQ.isLoading ? (
        <SkeletonBlock h={220} />
      ) : segments.length === 0 ? (
        <div className="empty-mini">Нет данных за период</div>
      ) : (
        <DonutChart
          segments={segments}
          totalMinor={byCatQ.data?.total_minor ?? '0'}
          currency={CURRENCY}
          kind={kind}
          onSegmentClick={(s) =>
            s.slug &&
            onDrillDown({
              period: period === 'year' ? undefined : period,
              kind,
              category: s.slug,
            })
          }
        />
      )}

      {pareto && pareto.count > 0 ? (
        <div className="pareto-card">
          <strong>
            {pareto.count} из {pareto.total}
          </strong>{' '}
          категорий дают <strong>80%</strong> {kind === 'expense' ? 'расходов' : 'доходов'}.
        </div>
      ) : null}

      <div className="section-subtitle">Поток денег</div>
      {sankeyQ.isLoading ? (
        <SkeletonBlock h={320} />
      ) : sankeyQ.data ? (
        <Sankey data={sankeyQ.data} currency={CURRENCY} />
      ) : null}

      <div className="section-subtitle">Топ мерчантов</div>
      {merchantsQ.isLoading ? (
        <SkeletonBlock h={150} />
      ) : !merchantsQ.data || merchantsQ.data.merchants.length === 0 ? (
        <div className="empty-mini">
          Мерчанты пока не извлечены. Добавьте несколько операций — бот распознает «Korzinka»,
          «Yandex Taxi» и подобные.
        </div>
      ) : (
        <div className="merchants-list">
          {merchantsQ.data.merchants.map((m) => (
            <div key={m.merchant} className="merchant-row">
              <CategoryMark
                name={m.top_category_name ?? m.merchant}
                slug={m.top_category_slug}
                size="sm"
              />
              <div className="merchant-main">
                <div className="merchant-name">{m.merchant}</div>
                <div className="merchant-meta">
                  {m.top_category_name ?? '—'} · {m.count} тр.
                </div>
              </div>
              <div className="merchant-amount">{formatWithCurrency(m.total_minor, CURRENCY)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="section-subtitle">Динамика по категориям (6 мес)</div>
      {trendsQ.isLoading ? (
        <SkeletonBlock h={300} />
      ) : trends.length === 0 ? (
        <div className="empty-mini">Нет истории</div>
      ) : (
        <div className="trends-list">
          {trends.slice(0, 12).map((t) => (
            <TrendRow
              key={t.category_slug ?? 'uncat'}
              t={t}
              kind={kind}
              onClick={() =>
                t.category_slug && onDrillDown({ period: 'month', kind, category: t.category_slug })
              }
            />
          ))}
        </div>
      )}
    </>
  );
}

function TrendRow({
  t,
  kind,
  onClick,
}: {
  t: CategoryTrend;
  kind: Kind;
  onClick: () => void;
}): JSX.Element {
  const sparkVals = t.sparkline.map((v) => Number(BigInt(v) / 100n));
  const color = kind === 'expense' ? 'var(--ink-2)' : 'var(--positive)';
  const trendCls = t.mom_pct == null ? '' : t.mom_pct >= 0 ? 'up' : 'down';
  return (
    <button type="button" className="trend-row" onClick={onClick}>
      <CategoryMark name={t.category_name} slug={t.category_slug} size="sm" />
      <div className="trend-main">
        <div className="trend-name">{t.category_name}</div>
        <Sparkline values={sparkVals} color={color} />
      </div>
      <div className="trend-numbers">
        <div className="trend-amount">{formatWithCurrency(t.current_minor, CURRENCY)}</div>
        {t.mom_pct != null ? (
          <div className={`trend-delta ${trendCls}`}>
            {t.mom_pct >= 0 ? '+' : ''}
            {t.mom_pct.toFixed(0)}%
          </div>
        ) : null}
      </div>
    </button>
  );
}

// ============================ BEHAVIOR TAB ==================================

function BehaviorTab(): JSX.Element {
  const heatmapQ = useQuery({
    queryKey: ['heatmap', 3],
    queryFn: () => api.calendarHeatmap(3),
  });
  const dowQ = useQuery({ queryKey: ['dow', 90], queryFn: () => api.dayOfWeek(90) });
  const streakQ = useQuery({ queryKey: ['streak'], queryFn: () => api.streak() });
  const anomQ = useQuery({
    queryKey: ['anomalies', 30],
    queryFn: () => api.anomalies(30),
  });
  const recurringQ = useQuery({ queryKey: ['recurring'], queryFn: () => api.recurring() });

  const days = (heatmapQ.data?.days ?? []).map((d) => ({
    date: d.date,
    amountMinor: d.spent_minor,
  }));

  const dowBuckets = (dowQ.data?.buckets ?? []).map((b) => ({
    dow: b.dow,
    amountMinor: b.spent_minor,
    count: b.count,
  }));

  const streak = streakQ.data;
  const anomalies = anomQ.data?.anomalies ?? [];

  return (
    <>
      <div className="metrics-grid">
        <MetricCard
          label="Серия без трат"
          value={streak ? `${streak.current_streak_days} дн` : '—'}
          hint={
            streak
              ? streak.best_streak_days > streak.current_streak_days
                ? `Рекорд: ${streak.best_streak_days} дн`
                : 'Это ваш рекорд'
              : ''
          }
          tone={streak && streak.current_streak_days >= 3 ? 'good' : 'neutral'}
          icon={<FlameIcon size={15} />}
        />
        <MetricCard
          label="Найдено выбросов"
          value={String(anomalies.length)}
          hint="за 30 дней"
          tone={anomalies.length > 5 ? 'warn' : 'neutral'}
          icon={<SearchIcon size={15} />}
        />
      </div>

      <div className="section-subtitle">Календарь трат (3 мес)</div>
      {heatmapQ.isLoading ? (
        <SkeletonBlock h={120} />
      ) : (
        <CalendarHeatmap days={days} currency={CURRENCY} />
      )}

      <div className="section-subtitle">По дням недели</div>
      {dowQ.isLoading ? (
        <SkeletonBlock h={140} />
      ) : (
        <DayOfWeekChart buckets={dowBuckets} currency={CURRENCY} />
      )}

      <div className="section-subtitle">Подписки и регулярные платежи</div>
      {recurringQ.isLoading ? (
        <SkeletonBlock h={140} />
      ) : !recurringQ.data || recurringQ.data.count === 0 ? (
        <div className="empty-mini">
          Регулярных платежей пока не найдено. Добавьте операцию вроде «Spotify 49000» — бот пометит
          её как повторяющуюся.
        </div>
      ) : (
        <>
          <div className="subscriptions-summary">
            <span>В сумме на подписки</span>
            <b>{formatWithCurrency(recurringQ.data.total_monthly_minor, CURRENCY)} / мес</b>
          </div>
          <div className="subscriptions-list">
            {recurringQ.data.items.map((s, i) => (
              <div key={i} className="sub-row">
                <CategoryMark
                  name={s.merchant ?? s.category_name ?? '?'}
                  slug={s.category_slug}
                  size="sm"
                />
                <div className="sub-main">
                  <div className="sub-name">{s.merchant ?? s.category_name ?? 'Без мерчанта'}</div>
                  <div className="sub-meta">
                    {s.period ?? 'monthly'} · последний раз{' '}
                    {new Date(s.last_date).toLocaleDateString('ru-RU')}
                  </div>
                </div>
                <div className="sub-amount">
                  {formatWithCurrency(s.monthly_estimate_minor, CURRENCY)}/мес
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="section-subtitle">Аномальные транзакции</div>
      {anomQ.isLoading ? (
        <SkeletonBlock h={120} />
      ) : anomalies.length === 0 ? (
        <div className="empty-mini">Все операции в пределах нормы.</div>
      ) : (
        <div className="anomaly-list">
          {anomalies.slice(0, 10).map((a) => (
            <div key={a.tx_id} className="anomaly-row">
              <CategoryMark
                name={a.category_name ?? a.category_slug ?? '?'}
                slug={a.category_slug}
                size="sm"
              />
              <div className="anomaly-main">
                <div className="anomaly-cat">
                  {a.category_name ?? a.category_slug ?? 'Без категории'}
                </div>
                <div className="anomaly-meta">
                  {a.note ? `${a.note} · ` : ''}×{a.multiplier.toFixed(1)} от обычной (~
                  {formatWithCurrency(a.mean_minor, CURRENCY)})
                </div>
              </div>
              <div className="anomaly-amount">{formatWithCurrency(a.amount_minor, CURRENCY)}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ============================ DEBTS TAB =====================================

function DebtsTab(): JSX.Element {
  const deepQ = useQuery({ queryKey: ['debt-deep'], queryFn: () => api.debtDeep() });
  const stratQ = useQuery({ queryKey: ['debt-strats'], queryFn: () => api.debtStrategies() });

  const deep = deepQ.data;

  if (deepQ.isLoading) return <SkeletonBlock h={400} />;
  if (!deep) return <div className="empty-mini">Нет долгов</div>;

  const debtFreeDate = deep.debt_free_date ? new Date(deep.debt_free_date) : null;
  const daysToFree = debtFreeDate
    ? Math.max(0, Math.round((debtFreeDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;

  return (
    <>
      {debtFreeDate ? (
        <div className="debt-free-banner">
          <div className="dfb-label">Свобода от долгов</div>
          <div className="dfb-date">{formatDateFull(debtFreeDate)}</div>
          {daysToFree != null ? <div className="dfb-sub">через {daysToFree} дней</div> : null}
        </div>
      ) : (
        <div className="empty-mini">
          При текущем сценарии долги не закрываются за 5 лет. Увеличьте досрочку.
        </div>
      )}

      <div className="metrics-grid">
        <MetricCard
          label="Долг всего"
          value={formatWithCurrency(deep.total_debt_minor, CURRENCY)}
          hint={`Из них компании: ${formatWithCurrency(
            (BigInt(deep.total_debt_minor) - BigInt(deep.total_debt_external_minor)).toString(),
            CURRENCY,
          )}`}
          tone="neutral"
          icon={<BankIcon size={15} />}
        />
        <MetricCard
          label="Средн. APR"
          value={`${(deep.weighted_apr_bp / 100).toFixed(1)}%`}
          hint="взвешенно по остатку"
          tone={
            deep.weighted_apr_bp >= 2500 ? 'bad' : deep.weighted_apr_bp >= 1500 ? 'warn' : 'good'
          }
          icon={<BarsIcon size={15} />}
        />
        <MetricCard
          label="Закрыто"
          value={`${deep.paid_progress_pct.toFixed(0)}%`}
          hint={`${formatWithCurrency(deep.paid_lifetime_minor, CURRENCY)} выплачено`}
          tone="good"
          icon={<FlagIcon size={15} />}
        />
        <MetricCard
          label="Месячная нагрузка"
          value={formatWithCurrency(deep.monthly_load_minor, CURRENCY)}
          hint="регулярных платежей"
          tone="neutral"
          icon={<CalendarIcon size={15} />}
        />
      </div>

      <div className="section-subtitle">Стратегия погашения</div>
      {stratQ.isLoading ? (
        <SkeletonBlock h={200} />
      ) : stratQ.data ? (
        <StrategyCompare data={stratQ.data} currency={CURRENCY} />
      ) : null}

      <div className="section-subtitle">Что если добавить досрочку</div>
      <WhatIfSlider baseMonthlyPrepayMinor={deep.base_monthly_prepay_minor} currency={CURRENCY} />

      {deep.time_to_zero.length > 0 ? (
        <>
          <div className="section-subtitle">Когда закроется каждый долг</div>
          <div className="ttz-list">
            {deep.time_to_zero
              .filter((t) => t.months_to_zero != null)
              .sort((a, b) => (a.months_to_zero ?? 0) - (b.months_to_zero ?? 0))
              .map((t) => (
                <div key={t.debt_id} className="ttz-row">
                  <div className="ttz-title">{t.title}</div>
                  <div className="ttz-months">{t.months_to_zero} мес</div>
                </div>
              ))}
          </div>
        </>
      ) : null}
    </>
  );
}

// ============================ HELPERS =======================================

function SkeletonBlock({ h = 220 }: { h?: number }): JSX.Element {
  return <div className="skeleton" style={{ height: h, marginBottom: 14 }} />;
}

const MONTHS_SHORT = [
  'Янв',
  'Фев',
  'Мар',
  'Апр',
  'Май',
  'Июн',
  'Июл',
  'Авг',
  'Сен',
  'Окт',
  'Ноя',
  'Дек',
];
const MONTHS_FULL = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];
const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];
const MONTHS_SHORT_RU = [
  'янв',
  'фев',
  'мар',
  'апр',
  'мая',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];

function bucketDayLabel(yyyyMmDd: string): string {
  return String(Number(yyyyMmDd.slice(8, 10)));
}
function bucketDayFull(yyyyMmDd: string): string {
  const [, m, d] = yyyyMmDd.split('-');
  if (!m || !d) return yyyyMmDd;
  return `${Number(d)} ${MONTHS_SHORT_RU[Number(m) - 1] ?? m}`;
}
function bucketMonthLabel(yyyyMm: string): string {
  return MONTHS_SHORT[Number(yyyyMm.slice(5, 7)) - 1] ?? yyyyMm;
}
function bucketMonthFull(yyyyMm: string): string {
  return MONTHS_FULL[Number(yyyyMm.slice(5, 7)) - 1] ?? yyyyMm;
}
function formatDateFull(d: Date): string {
  return `${d.getUTCDate()} ${MONTHS_GENITIVE[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
